/**
 * Shared zone-data schema between the editor (which writes it) and the
 * game runtime (which reads it). The validator returns a typed `Zone`
 * or throws a clear error so import dialogs can surface it to the user.
 */

export const CURRENT_ZONE_SCHEMA = 1 as const;

export type ZoneAge = 'stone' | 'bronze' | 'iron' | 'classical' | 'medieval' | 'industrial' | 'information';

export const TILE_KINDS = ['grass', 'tall-grass', 'dirt', 'sand', 'stone'] as const;
export type TileKind = (typeof TILE_KINDS)[number];

export const OBJECT_KINDS = [
  'tree',
  'bush',
  'boulder',
  'rock',
  'flower',
  'fire-pit',
  'town-stone',
  'blade',
] as const;
export type ObjectKind = (typeof OBJECT_KINDS)[number];

export const NPC_TEMPLATES = ['lucy', 'java-man', 'mungo', 'cro-magnon', 'otzi'] as const;
export type NpcTemplateId = (typeof NPC_TEMPLATES)[number];

export const NPC_BEHAVIORS = ['wander', 'patrol'] as const;
export type NpcBehavior = (typeof NPC_BEHAVIORS)[number];

export interface Waypoint {
  x: number;
  z: number;
}

export interface ObjectEntry {
  type: ObjectKind;
  x: number;
  z: number;
  scale?: number;
  rotY?: number;
  /** Optional tint override, components in [0, 2]. */
  tint?: [number, number, number];
}

export interface NpcEntry {
  templateId: NpcTemplateId;
  x: number;
  z: number;
  behavior: NpcBehavior;
  patrol?: Waypoint[];
}

export interface TownEntry {
  name: string;
  x: number;
  z: number;
}

export interface PathEntry {
  name: string;
  points: Waypoint[];
}

export interface ZoneSize {
  width: number;
  height: number;
  tileSize: number;
}

export interface Zone {
  schema: 1;
  id: string;
  name: string;
  age: ZoneAge;
  size: ZoneSize;
  tilePalette: TileKind[];
  /** Row-major `width * height` indices into tilePalette. */
  tiles: number[];
  objects: ObjectEntry[];
  npcs: NpcEntry[];
  towns: TownEntry[];
  paths: PathEntry[];
  spawn: Waypoint;
}

export interface NewZoneOptions {
  id: string;
  name: string;
  age: ZoneAge;
  width: number;
  height: number;
  tileSize: number;
}

export const createEmptyZone = (opts: NewZoneOptions): Zone => {
  const total = opts.width * opts.height;
  const tiles = new Array<number>(total).fill(0);
  return {
    schema: CURRENT_ZONE_SCHEMA,
    id: opts.id,
    name: opts.name,
    age: opts.age,
    size: { width: opts.width, height: opts.height, tileSize: opts.tileSize },
    tilePalette: ['grass', 'tall-grass', 'dirt'],
    tiles,
    objects: [],
    npcs: [],
    towns: [],
    paths: [],
    spawn: { x: 0, z: 0 },
  };
};

export class ZoneValidationError extends Error {
  constructor(message: string, readonly path: string) {
    super(`zone: ${path}: ${message}`);
    this.name = 'ZoneValidationError';
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object';

const expectNumber = (v: unknown, path: string): number => {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new ZoneValidationError(`expected finite number, got ${typeof v}`, path);
  }
  return v;
};

const expectString = (v: unknown, path: string): string => {
  if (typeof v !== 'string') throw new ZoneValidationError(`expected string`, path);
  return v;
};

const expectArray = (v: unknown, path: string): unknown[] => {
  if (!Array.isArray(v)) throw new ZoneValidationError(`expected array`, path);
  return v;
};

const expectRecord = (v: unknown, path: string): Record<string, unknown> => {
  if (!isRecord(v)) throw new ZoneValidationError(`expected object`, path);
  return v;
};

const expectIn = <T extends string>(v: unknown, allowed: readonly T[], path: string): T => {
  const s = expectString(v, path);
  if (!allowed.includes(s as T)) {
    throw new ZoneValidationError(`expected one of ${allowed.join(', ')}, got "${s}"`, path);
  }
  return s as T;
};

const parseWaypoint = (raw: unknown, path: string): Waypoint => {
  const r = expectRecord(raw, path);
  return { x: expectNumber(r.x, `${path}.x`), z: expectNumber(r.z, `${path}.z`) };
};

const parseTint = (raw: unknown, path: string): [number, number, number] => {
  const arr = expectArray(raw, path);
  if (arr.length !== 3) throw new ZoneValidationError(`expected 3-tuple`, path);
  return [
    expectNumber(arr[0], `${path}[0]`),
    expectNumber(arr[1], `${path}[1]`),
    expectNumber(arr[2], `${path}[2]`),
  ];
};

const parseObject = (raw: unknown, path: string): ObjectEntry => {
  const r = expectRecord(raw, path);
  const out: ObjectEntry = {
    type: expectIn(r.type, OBJECT_KINDS, `${path}.type`),
    x: expectNumber(r.x, `${path}.x`),
    z: expectNumber(r.z, `${path}.z`),
  };
  if (r.scale !== undefined) out.scale = expectNumber(r.scale, `${path}.scale`);
  if (r.rotY !== undefined) out.rotY = expectNumber(r.rotY, `${path}.rotY`);
  if (r.tint !== undefined) out.tint = parseTint(r.tint, `${path}.tint`);
  return out;
};

const parseNpc = (raw: unknown, path: string): NpcEntry => {
  const r = expectRecord(raw, path);
  const out: NpcEntry = {
    templateId: expectIn(r.templateId, NPC_TEMPLATES, `${path}.templateId`),
    x: expectNumber(r.x, `${path}.x`),
    z: expectNumber(r.z, `${path}.z`),
    behavior: expectIn(r.behavior, NPC_BEHAVIORS, `${path}.behavior`),
  };
  if (r.patrol !== undefined) {
    const patrol = expectArray(r.patrol, `${path}.patrol`);
    out.patrol = patrol.map((p, i) => parseWaypoint(p, `${path}.patrol[${i}]`));
  }
  return out;
};

const parseTown = (raw: unknown, path: string): TownEntry => {
  const r = expectRecord(raw, path);
  return {
    name: expectString(r.name, `${path}.name`),
    x: expectNumber(r.x, `${path}.x`),
    z: expectNumber(r.z, `${path}.z`),
  };
};

const parsePath = (raw: unknown, path: string): PathEntry => {
  const r = expectRecord(raw, path);
  const pts = expectArray(r.points, `${path}.points`);
  return {
    name: expectString(r.name, `${path}.name`),
    points: pts.map((p, i) => parseWaypoint(p, `${path}.points[${i}]`)),
  };
};

/**
 * Strictly parse a raw unknown payload into a typed Zone or throw a
 * ZoneValidationError with a path that pinpoints the bad field. Future
 * schema versions register migrations here.
 */
export const parseZone = (raw: unknown): Zone => {
  const r = expectRecord(raw, '$');
  const schema = expectNumber(r.schema, '$.schema');
  if (schema !== CURRENT_ZONE_SCHEMA) {
    throw new ZoneValidationError(
      `unsupported schema ${schema}; expected ${CURRENT_ZONE_SCHEMA}`,
      '$.schema',
    );
  }

  const sizeRaw = expectRecord(r.size, '$.size');
  const size: ZoneSize = {
    width: expectNumber(sizeRaw.width, '$.size.width'),
    height: expectNumber(sizeRaw.height, '$.size.height'),
    tileSize: expectNumber(sizeRaw.tileSize, '$.size.tileSize'),
  };
  if (size.width <= 0 || size.height <= 0 || size.tileSize <= 0) {
    throw new ZoneValidationError('size dimensions must be > 0', '$.size');
  }

  const tilePaletteRaw = expectArray(r.tilePalette, '$.tilePalette');
  const tilePalette: TileKind[] = tilePaletteRaw.map((t, i) =>
    expectIn(t, TILE_KINDS, `$.tilePalette[${i}]`),
  );

  const tilesRaw = expectArray(r.tiles, '$.tiles');
  if (tilesRaw.length !== size.width * size.height) {
    throw new ZoneValidationError(
      `tiles length ${tilesRaw.length} !== width*height ${size.width * size.height}`,
      '$.tiles',
    );
  }
  const tiles: number[] = new Array<number>(tilesRaw.length);
  for (let i = 0; i < tilesRaw.length; i++) {
    const v = tilesRaw[i];
    if (typeof v !== 'number' || !Number.isInteger(v)) {
      throw new ZoneValidationError(`expected integer`, `$.tiles[${i}]`);
    }
    if (v < 0 || v >= tilePalette.length) {
      throw new ZoneValidationError(
        `tile index ${v} out of range [0, ${tilePalette.length})`,
        `$.tiles[${i}]`,
      );
    }
    tiles[i] = v;
  }

  const objectsRaw = expectArray(r.objects, '$.objects');
  const objects = objectsRaw.map((o, i) => parseObject(o, `$.objects[${i}]`));

  const npcsRaw = expectArray(r.npcs, '$.npcs');
  const npcs = npcsRaw.map((n, i) => parseNpc(n, `$.npcs[${i}]`));

  const townsRaw = expectArray(r.towns, '$.towns');
  const towns = townsRaw.map((t, i) => parseTown(t, `$.towns[${i}]`));

  const pathsRaw = expectArray(r.paths, '$.paths');
  const paths = pathsRaw.map((p, i) => parsePath(p, `$.paths[${i}]`));

  return {
    schema: CURRENT_ZONE_SCHEMA,
    id: expectString(r.id, '$.id'),
    name: expectString(r.name, '$.name'),
    age: expectIn(r.age, ['stone', 'bronze', 'iron', 'classical', 'medieval', 'industrial', 'information'], '$.age'),
    size,
    tilePalette,
    tiles,
    objects,
    npcs,
    towns,
    paths,
    spawn: parseWaypoint(r.spawn, '$.spawn'),
  };
};

/** Tile-grid → world-space helpers. The world origin sits at the centre of
 *  the map. */
export const tileToWorldX = (tx: number, size: ZoneSize): number =>
  (tx + 0.5) * size.tileSize - (size.width * size.tileSize) / 2;

export const tileToWorldZ = (tz: number, size: ZoneSize): number =>
  (tz + 0.5) * size.tileSize - (size.height * size.tileSize) / 2;

export const worldToTileX = (x: number, size: ZoneSize): number =>
  Math.floor((x + (size.width * size.tileSize) / 2) / size.tileSize);

export const worldToTileZ = (z: number, size: ZoneSize): number =>
  Math.floor((z + (size.height * size.tileSize) / 2) / size.tileSize);
